import { fileTypeFromBlob } from 'file-type';
import { getFileExtension } from '@app/shared/helpers/file';
import {
  extensionAliases,
  ignoredExtensions,
  ResourceMimeType,
  supportedNonStandardExtensions,
} from '@app-types/blackroom-app/resourceMimeType';

/**
 * List of supported file extensions derived from ResourceMimeType enum keys,
 * extension aliases, and supportedNonStandardExtensions.
 */
const supportedExtensions = [
  ...Object.keys(ResourceMimeType),
  ...Object.keys(extensionAliases),
  ...supportedNonStandardExtensions,
];

/**
 * Set of supported MIME types for quick lookup
 */
const supportedMimeTypes = new Set(
  Object.values(ResourceMimeType).map((m) => m.toLowerCase()),
);

/**
 * Text-based MIME types that file-type library cannot detect (no magic bytes)
 */
const textBasedMimeTypes = new Set([
  'text/markdown',
  'text/plain',
  'text/csv',
  'message/rfc822',
]);

/**
 * Old Office extensions that use CFB (Compound File Binary) container format.
 * file-type library detects these as 'application/x-cfb' instead of their actual MIME types.
 */
const cfbOfficeExtensions = new Set(['doc', 'xls', 'ppt']);

/**
 * OOXML-based Office extensions that share the same ZIP container format.
 * file-type library may detect these as a generic Office format (e.g., xlsx for xlsb)
 * because they share similar magic bytes.
 */
const ooxmlOfficeExtensions = new Set([
  'docx',
  'docm',
  'dotx',
  'dotm',
  'xlsx',
  'xlsm',
  'xlsb',
  'xltx',
  'xltm',
  'pptx',
  'pptm',
  'potx',
  'ppsx',
]);

/**
 * MIME types that indicate an OOXML Office format (or generic ZIP).
 * When these are detected and the original extension is a known OOXML format,
 * we should use the MIME type based on the original extension.
 */
const ooxmlDetectedMimeTypes = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
]);

/**
 * Check if a file extension is supported (including aliases).
 */
function isExtensionSupported(extension: string): boolean {
  return supportedExtensions.includes(extension.toLowerCase());
}

/**
 * Check if a MIME type is supported.
 */
function isMimeTypeSupported(mimeType: string): boolean {
  return supportedMimeTypes.has(mimeType.toLowerCase());
}

/**
 * Get the File object from a DataTransferItem or File
 */
function getFileFromInput(input: DataTransferItem | File): File | null {
  if (input instanceof File) {
    return input;
  }
  return input.getAsFile();
}

/**
 * Synchronous validation for UI feedback during drag-and-drop.
 * Uses browser-provided MIME type and extension checks.
 * For actual upload validation, use validateUploadFileAsync.
 */
export function validateUploadFileSync(file: DataTransferItem | File): {
  validation: boolean;
  extension?: string;
} {
  if (file instanceof File) {
    const extension = getFileExtension(file.name);

    if (file.name === '.DS_Store') {
      return { validation: false };
    }

    const hasSupportedMimeType = isMimeTypeSupported(file.type);
    const hasSupportedExtension = isExtensionSupported(extension);

    return {
      validation:
        hasSupportedMimeType ||
        hasSupportedExtension ||
        ignoredExtensions.includes(extension),
      extension,
    };
  }

  const entry = file.webkitGetAsEntry();
  const extension = getFileExtension(entry?.name ?? '');

  if (entry?.name === '.DS_Store') {
    return { validation: false };
  }

  const hasSupportedMimeType = isMimeTypeSupported(file.type);
  const hasSupportedExtension = isExtensionSupported(extension);

  return {
    validation:
      entry?.isDirectory ||
      hasSupportedMimeType ||
      hasSupportedExtension ||
      ignoredExtensions.includes(extension),
    extension,
  };
}

/**
 * Validate a file by checking its real MIME type using magic bytes.
 * This is the primary validation function that should be used for all file validation.
 *
 * @returns Promise with validation result and detected extension
 */
export async function validateUploadFileAsync(
  input: DataTransferItem | File,
): Promise<{
  validation: boolean;
  extension?: string;
  detectedMimeType?: string;
}> {
  if (!(input instanceof File)) {
    const entry = input.webkitGetAsEntry?.() ?? null;
    if (entry?.isDirectory) {
      return { validation: true };
    }
  }

  const file = getFileFromInput(input);

  if (!file) {
    return { validation: false };
  }

  const extension = getFileExtension(file.name);
  const extensionLower = extension.toLowerCase();

  // Skip .DS_Store files
  if (file.name === '.DS_Store') {
    return { validation: false, extension };
  }

  // Skip ignored extensions silently
  if (ignoredExtensions.includes(extension)) {
    return { validation: true, extension };
  }

  // Try to detect the real MIME type using magic bytes
  try {
    const detectedType = await fileTypeFromBlob(file);

    if (detectedType) {
      // We have a detected MIME type from magic bytes
      // Check both:
      // 1. The detected MIME type is supported
      // 2. The original file extension is supported (prevents spoofed files)
      const isMimeSupported = isMimeTypeSupported(detectedType.mime);
      const isOriginalExtSupported = isExtensionSupported(extension);

      // Special case: Old Office files (.doc, .xls, .ppt) use CFB container format
      // file-type detects them as 'application/x-cfb' instead of their actual MIME types
      const isCfbOldOfficeFile =
        detectedType.mime === 'application/x-cfb' &&
        cfbOfficeExtensions.has(extension.toLowerCase());

      // Special case: OOXML Office formats share the same ZIP container
      // file-type may detect xlsb as xlsx, docm as docx, etc.
      // When detected MIME is an OOXML type and original extension is a known OOXML format,
      // use the MIME type based on the original extension for accurate icon display
      const isOoxmlMisdetected =
        ooxmlDetectedMimeTypes.has(detectedType.mime) &&
        ooxmlOfficeExtensions.has(extension.toLowerCase());

      // Determine the correct MIME type to return
      let finalMimeType: string;
      const extLower = extension.toLowerCase();
      if (isCfbOldOfficeFile || isOoxmlMisdetected) {
        finalMimeType =
          ResourceMimeType[extLower as keyof typeof ResourceMimeType];
      } else {
        finalMimeType = detectedType.mime;
      }

      return {
        validation:
          (isMimeSupported || isCfbOldOfficeFile || isOoxmlMisdetected) &&
          isOriginalExtSupported,
        extension,
        detectedMimeType: finalMimeType,
      };
    }

    // file-type couldn't detect the type (common for text-based files)
    // Fall back to checking if it's a text-based file type
    const browserMimeType = file.type.toLowerCase();

    // If browser reports a text-based MIME type we support, accept it
    if (browserMimeType && textBasedMimeTypes.has(browserMimeType)) {
      return {
        validation: true,
        extension,
        detectedMimeType: browserMimeType,
      };
    }

    // For empty MIME type (browser couldn't detect), check extension for text types
    if (!browserMimeType || browserMimeType === '') {
      // Resolve extension alias if needed
      const resolvedExtension =
        extensionAliases[extensionLower] ??
        (extensionLower as keyof typeof ResourceMimeType);
      const mimeFromExtension = ResourceMimeType[resolvedExtension];

      if (mimeFromExtension && textBasedMimeTypes.has(mimeFromExtension)) {
        return {
          validation: true,
          extension,
          detectedMimeType: mimeFromExtension,
        };
      }

      // Check for non-standard extensions
      if (supportedNonStandardExtensions.includes(extensionLower)) {
        return { validation: true, extension };
      }
    }

    // Browser provided a MIME type - check if it's supported
    if (browserMimeType && isMimeTypeSupported(browserMimeType)) {
      return {
        validation: true,
        extension,
        detectedMimeType: browserMimeType,
      };
    }

    // Nothing matched - file type not supported
    return { validation: false, extension };
  } catch {
    // Error during detection - reject the file for safety
    return { validation: false, extension };
  }
}
